// i18n
import { translate, translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import styled from '@emotion/styled';
import ServicePageTemplate from 'presentations/views/components/templates/ServicePageTemplate';
import { background, primary, white } from 'presentations/views/styles/color';
import {
  borderRadius,
  fontSize,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import Image from 'presentations/views/components/atoms/Image';
import Button from 'presentations/views/components/atoms/Button';
import Icon from 'presentations/views/components/atoms/Icon';
import Header from 'presentations/views/components/organisms/Header';
import { ViewerItem } from 'presentations/api/schema/user';
import Headline from 'presentations/views/components/atoms/Headline';
import ContentItem4 from 'presentations/views/components/molecules/ServiceContentItem4';
import BackgroundContainer from 'presentations/views/components/organisms/service/BackgroundContainer';
import {
  dashboardPath,
  integrationPath,
  signUpPath,
  webPath,
} from 'presentations/routes/path';
import { IntegrationType } from 'domains/models/account/providerType';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

interface Props {
  viewer: ViewerItem | null;
  id: IntegrationType;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const router = useRouter();
  const serviceName = translate(`IntegrationType_${props.id}`);
  const symbolImage = useMemo(() => {
    switch (props.id) {
      case IntegrationType.HubSpot:
        return 'pages/top/integration/hubspot.svg';
      case IntegrationType.Zoom:
        return 'pages/top/integration/zoom.svg';
      default:
        break;
    }
    return null;
  }, [props.id]);

  const buttonList = (
    <ButtonList>
      <SignUpButton
        color="primary"
        size="large"
        href={signUpPath({
          to: webPath(dashboardPath()),
          locale: router.locale,
        })}
      >
        {t('Sign Up')}
      </SignUpButton>
      {props.viewer ? (
        <AddButton
          color="primary"
          size="large"
          variant="outlined"
          href={integrationPath(props.id)}
        >
          {t('Add Integration', { serviceName })}
        </AddButton>
      ) : (
        <AddButton
          color="primary"
          size="large"
          variant="outlined"
          href={signUpPath({
            to: webPath(dashboardPath()),
            locale: router.locale,
          })}
        >
          {t('Add Integration', { serviceName })}
        </AddButton>
      )}
    </ButtonList>
  );

  return (
    <ServicePageTemplate
      isVisibleSupport={true}
      header={<Header viewer={props.viewer} />}
      breadcrumbs={[]}
    >
      <BackgroundContainer>
        <MainVisual>
          <Title variant="h1">{t('page title', { serviceName })}</Title>
          <ServiceList>
            <ServiceContainer>
              {symbolImage && (
                <ServiceImage
                  src={symbolImage}
                  alt={serviceName}
                  width={48}
                  height={48}
                />
              )}
              <ServiceTitle>{serviceName}</ServiceTitle>
            </ServiceContainer>
            <ServicePlusContainer>
              <ServicePlusIcon name="plus-circle-outline" />
            </ServicePlusContainer>
            <ServiceContainer>
              <ServiceImage
                src="logo/symbol.svg"
                alt={translate('siteName')}
                width={48}
                height={48}
              />
              <ServiceTitle>Jicoo</ServiceTitle>
            </ServiceContainer>
          </ServiceList>
          <Description>{t(`description ${props.id}`)}</Description>
          {buttonList}
        </MainVisual>
      </BackgroundContainer>
      <Container>
        <SectionTitle variant="h3">{t('Features')}</SectionTitle>
        <ListContainer>
          <ListItem>
            <ContentItem4
              title={t(`feature_titles_${props.id}.0`)}
              description={t(`feature_texts_${props.id}.0`)}
            />
          </ListItem>
          <ListItem>
            <ContentItem4
              title={t(`feature_titles_${props.id}.1`)}
              description={t(`feature_texts_${props.id}.1`)}
            />
          </ListItem>
          <ListItem>
            <ContentItem4
              title={t(`feature_titles_${props.id}.2`)}
              description={t(`feature_texts_${props.id}.2`)}
            />
          </ListItem>
        </ListContainer>
        {buttonList}
      </Container>
    </ServicePageTemplate>
  );
}

const MainVisual = styled.div`
  margin: 0 auto;
  padding: ${spacing(64)} 0 ${spacing(128)};
  ${mediaMax.tablet} {
    padding: ${spacing(32)} 0 ${spacing(64)};
  }
`;

const ServiceList = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${spacing(48)};
  ${mediaMax.tablet} {
    margin: 0 ${spacing(12)} ${spacing(48)};
  }
`;
const ButtonList = styled.div`
  display: flex;
  justify-content: center;
  ${mediaMax.tablet} {
    flex-direction: column;
    margin: 0 ${spacing(12)};
  }
`;
const ServiceContainer = styled.div`
  box-shadow: 0px 10px 30px rgba(102, 102, 102, 0.15);
  border-radius: ${borderRadius(16)};
  padding: 0 ${spacing(32)};
  min-width: 140px;
  height: 140px;
  background-color: ${white};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;
const ServiceImage = styled(Image)`
  margin-bottom: ${spacing(8)};
`;
const ServicePlusContainer = styled.div`
  padding: 0 ${spacing(20)};
`;
const ServicePlusIcon = styled(Icon)`
  width: ${fontSize(32)} !important;
  height: ${fontSize(32)} !important;
`;
const ServiceTitle = styled.div`
  font-weight: bold;
  font-size: ${fontSize(18)};
`;

const Container = styled.div`
  background-color: ${background};
  max-width: 1080px;
  margin: 0 auto ${spacing(128)};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(40)};
  }
`;
const Title = styled(Headline)`
  font-weight: bold;
  font-size: ${fontSize(40)};
  display: flex;
  justify-content: center;
  margin-bottom: ${spacing(40)};
  ${mediaMax.tablet} {
    font-size: ${fontSize(24)};
    margin: 0 ${spacing(12)} ${spacing(40)};
  }
`;
const SectionTitle = styled(Headline)`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: ${spacing(40)};
  font-size: ${fontSize(28)};
  ${mediaMax.tablet} {
    font-size: ${fontSize(18)};
  }
`;
const Description = styled.div`
  font-size: ${fontSize(14)};
  display: flex;
  justify-content: center;
  margin: 0 ${spacing(16)} ${spacing(40)};
  ${mediaMax.tablet} {
    margin: 0 ${spacing(12)} ${spacing(40)};
  }
`;

const ListContainer = styled.div`
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  margin: 0 ${spacing(16)} ${spacing(64)};
  ${mediaMax.tablet} {
    display: block;
    margin: 0 ${spacing(12)} ${spacing(64)};
  }
`;

const ListItem = styled.div`
  width: 32%;
  height: auto;
  ${mediaMax.tablet} {
    width: 100%;
    margin-bottom: ${spacing(24)};
  }
`;
const SignUpButton = styled(Button)`
  width: 200px;
  ${mediaMax.tablet} {
    width: 100%;
  }
`;
const AddButton = styled(Button)`
  margin-left: ${spacing(24)};
  min-width: 200px;
  ${mediaMax.tablet} {
    width: 100%;
    margin-left: 0;
    margin-top: ${spacing(16)};
  }
`;
